// ─────────────────────────────────────────────────────────────────────────────
// Pure, dependency-free logic for SearchableSelect.
//
// The React component owns DOM/refs/effects; the filtering and keyboard-highlight
// math live here so they can be unit-tested under `node --test` (interaction
// behavior that can't be exercised via server-side render).
// ─────────────────────────────────────────────────────────────────────────────

export interface SearchableOption {
  value: string;
  label: string;
  /** Optional secondary text shown + searched (e.g. department). */
  description?: string;
}

/**
 * Case-insensitive substring filter over label + description. A blank query
 * returns a shallow copy of all options (never mutates the input).
 */
export function filterSelectOptions(options: readonly SearchableOption[], query: string): SearchableOption[] {
  const q = String(query ?? '').trim().toLowerCase();
  if (!q) return options.slice();
  return options.filter(
    (o) => o.label.toLowerCase().includes(q) || (o.description ?? '').toLowerCase().includes(q),
  );
}

/**
 * Compute the next highlighted index for keyboard navigation.
 *   • Empty list → -1 (nothing to highlight).
 *   • No current highlight (-1) → first item when moving down, last when up.
 *   • Otherwise wraps around the ends.
 */
export function moveHighlight(current: number, direction: 1 | -1, length: number): number {
  if (length <= 0) return -1;
  if (current < 0) return direction > 0 ? 0 : length - 1;
  return (current + direction + length) % length;
}

/** Clamp a highlight index into the valid range for a list of `length`. */
export function clampHighlight(index: number, length: number): number {
  if (length <= 0) return -1;
  if (index < 0) return 0;
  if (index >= length) return length - 1;
  return index;
}

/** Stable DOM id for an option row, used by aria-activedescendant. */
export function optionDomId(baseId: string, value: string): string {
  return `${baseId}-opt-${value}`;
}
